/* eslint-disable camelcase */
import React, { useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
// components
import { getStaffMembers } from '../store/actions/users';
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import SearchNotFound from '../components/SearchNotFound';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import { hasPermission, PERMISSIONS } from '../utils/hasPermission';

// ----------------------------------------------------------------------

const avatar = '/static/mock-images/avatars/avatar_default.jpg';

const TABLE_HEAD = [
  { id: 'username', label: 'Name / Username', alignRight: false },
  { id: 'email', label: 'Email Address', alignRight: false },
  { id: 'phone', label: 'Phone Number', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'permissions', label: 'Permission Overrides', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        _user.email.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user.username.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user.phone.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const getRoleColor = (role) => {
  if (role === 'admin') return 'secondary';
  if (role === 'manager') return 'info';
  if (role === 'finance') return 'warning';
  if (role === 'developer') return 'success';
  return 'default';
};

const getStatusColor = (status) => {
  if (status === 'active') return 'success';
  if (status === 'pending') return 'warning';
  return 'error';
};

const getPermissionBadges = (role, customPermissions = [], deniedPermissions = []) => {
  if (role === 'admin') {
    return <Chip label="Full Access (*)" size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.75rem', fontWeight: 600 }} />;
  }

  const hasCustom = customPermissions.length > 0;
  const hasDenied = deniedPermissions.length > 0;

  if (!hasCustom && !hasDenied) {
    return (
      <Typography variant="caption" color="text.secondary">
        Role Standard
      </Typography>
    );
  }

  return (
    <>
      {hasCustom && (
        <Tooltip title={`Added: ${customPermissions.join(', ')}`}>
          <Chip label={`+${customPermissions.length} custom`} size="small" color="primary" sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
        </Tooltip>
      )}
      {hasDenied && (
        <Tooltip title={`Revoked: ${deniedPermissions.join(', ')}`}>
          <Chip label={`-${deniedPermissions.length} revoked`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
        </Tooltip>
      )}
    </>
  );
};

export default function User() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { staffMembers } = useSelector((state) => state.users);

  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState('asc');
  const [selected, setSelected] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState('name');
  const [filterName, setFilterName] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Modal states
  const [userClicked, setUserClicked] = React.useState(false);
  const [userClickId, setUserClickId] = React.useState(null);
  const [createStaffModal, setCreateStaffModal] = useState(false);

  const handleClickUser = (id) => {
    if (hasPermission(user, PERMISSIONS.STAFF_MANAGE)) {
      navigate(`/dashboard/staff/edit/${id}`);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  React.useEffect(() => {
    dispatch(getStaffMembers());
  }, [dispatch, navigate, user?.role]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - staffMembers.length) : 0;
  const filteredUsers = applySortFilter(staffMembers, getComparator(order, orderBy), filterName);
  const isUserNotFound = filteredUsers.length === 0;

  // Compute stats on-the-fly from Redux state
  const totalStaff = staffMembers.length;
  const activeStaff = staffMembers.filter((m) => m.status === 'active').length;
  const pendingStaff = staffMembers.filter((m) => m.status === 'pending').length;

  return (
    <Page title="Staff & Permissions">
      <Container maxWidth="lg">
        {/* Header Title Section */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E1E2F' }}>
              Staff & Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage team members, roles, and granular access permissions
            </Typography>
          </div>
          {hasPermission(user, PERMISSIONS.STAFF_MANAGE) && (
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard/staff/create')}
              startIcon={<Iconify icon="ph:user-plus-bold" />}
              sx={{
                borderRadius: '8px',
                fontWeight: 600,
                boxShadow: 'none',
                background: '#5D5FEF',
                px: 2.5,
                py: 1.2,
                '&:hover': {
                  background: '#4B4DCC',
                  boxShadow: '0px 4px 10px rgba(93, 95, 239, 0.3)'
                }
              }}
            >
              Add New Staff
            </Button>
          )}
        </Stack>

        {/* Dashboard Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderRadius: '12px',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
              background: '#FFFFFF',
              borderLeft: '5px solid #5D5FEF',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Team Members
                </Typography>
                <Typography variant="h3" sx={{ mt: 1, fontWeight: 700, color: '#1E1E2F' }}>
                  {totalStaff}
                </Typography>
              </div>
              <Iconify icon="ph:users-three-light" width={40} height={40} color="#5D5FEF" />
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderRadius: '12px',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
              background: '#FFFFFF',
              borderLeft: '5px solid #10B981',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Active Staff
                </Typography>
                <Typography variant="h3" sx={{ mt: 1, fontWeight: 700, color: '#1E1E2F' }}>
                  {activeStaff}
                </Typography>
              </div>
              <Iconify icon="ph:user-circle-gear-light" width={40} height={40} color="#10B981" />
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              p: 3, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderRadius: '12px',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
              background: '#FFFFFF',
              borderLeft: '5px solid #F59E0B',
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <div>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Pending Invites
                </Typography>
                <Typography variant="h3" sx={{ mt: 1, fontWeight: 700, color: '#1E1E2F' }}>
                  {pendingStaff}
                </Typography>
              </div>
              <Iconify icon="ph:envelope-simple-open-light" width={40} height={40} color="#F59E0B" />
            </Card>
          </Grid>
        </Grid>

        {/* Staff Table Card */}
        <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={staffMembers.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, username, firstName, role, status, email, phone, customPermissions = [], deniedPermissions = [] } = row;

                    const hasCustom = customPermissions.length > 0;
                    const hasDenied = deniedPermissions.length > 0;

                    return (
                      <TableRow
                        hover
                        key={id}
                        tabIndex={-1}
                        onClick={() => handleClickUser(id)}
                        sx={{ cursor: 'pointer', transition: 'background-color 0.2s ease-in-out' }}
                      >
                        <TableCell padding="checkbox">{}</TableCell>
                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={firstName} src={avatar} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2C3E50' }} noWrap>
                              {username}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">{email}</TableCell>
                        <TableCell align="left">{phone || '—'}</TableCell>
                        <TableCell align="left">
                          <Label 
                            variant="outlined" 
                            color={getRoleColor(role)}
                          >
                            {sentenceCase(role)}
                          </Label>
                        </TableCell>
                        <TableCell align="left">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {getPermissionBadges(role, customPermissions, deniedPermissions)}
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          <Label 
                            variant="ghost" 
                            color={getStatusColor(status)}
                          >
                            {sentenceCase(status)}
                          </Label>
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                const actorKey = row.userId || row.email || row.id || id;
                                navigate(`/dashboard/audit-logs?actorId=${actorKey}`);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Activity Log
                            </Button>
                            <Button size="small" variant="outlined" color="inherit" sx={{ textTransform: 'none' }}>
                              Manage
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={7} />
                    </TableRow>
                  )}
                </TableBody>

                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={staffMembers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
